/**
 * Blog Utility Helpers
 * Safe extraction and formatting of blog post data
 */

import { IBlogPost } from '@/types/blog'

/**
 * Safely extract category name from various formats
 * Handles: string, object with name property
 */
export const getCategoryName = (category: any): string => {
    if (!category) return ''

    if (typeof category === 'string') {
        return category
    }

    if (typeof category === 'object' && category?.name) {
        return category.name
    }

    return ''
}

/**
 * Safely extract category ID from various formats
 */
export const getCategoryId = (category: any): string => {
    if (!category) return ''

    if (typeof category === 'string') {
        return category
    }

    if (typeof category === 'object' && category?._id) {
        return category._id
    }

    return ''
}

/**
 * Safe tag rendering - ensures tags is always an array
 */
export const getSafeTags = (tags: any): string[] => {
    if (!Array.isArray(tags)) {
        return []
    }
    return tags.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)
}

/**
 * Format date string to readable format
 */
export const formatBlogDate = (dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    } catch {
        return 'N/A'
    }
}

/**
 * Validate blog post data before rendering
 * Returns true if essential fields are present and valid
 */
export const isValidBlogPost = (post: any): post is IBlogPost => {
    return (
        post &&
        typeof post._id === 'string' &&
        typeof post.title === 'string' &&
        typeof post.excerpt === 'string' &&
        (typeof post.authorName === 'string' || typeof post.author === 'string')
    )
}

/**
 * Sanitize blog content - removes or escapes dangerous HTML
 */
export const sanitizeBlogContent = (content: string): string => {
    if (!content) return ''

    // Remove script tags and content
    return content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
}

/**
 * Generate excerpt from content if not provided
 */
export const generateExcerpt = (content: string, length: number = 150): string => {
    if (!content) return ''

    // Remove HTML tags
    const text = content.replace(/<[^>]*>/g, '')
    // Trim to specified length and add ellipsis if needed
    if (text.length > length) {
        return text.substring(0, length).trim() + '...'
    }
    return text
}

/**
 * Calculate reading time based on content word count
 * Assumes average reading speed of 200 words per minute
 */
export const calculateReadingTime = (content: string): number => {
    if (!content) return 1

    const wordCount = content.trim().split(/\s+/).length
    const readingTime = Math.ceil(wordCount / 200)
    return Math.max(1, Math.min(readingTime, 120)) // Between 1-120 minutes
}